import React from "react";

const Section = ({ children, title }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl font-semibold text-[#0a0a0a] mb-3 pb-3 border-b border-gray-200">{title}</h2>
    <div className="text-gray-700 leading-relaxed font-light space-y-3 max-w-3xl">{children}</div>
  </section>
);

export const LegalLayout = ({ kicker, title, lastUpdated, children, testid }) => (
  <div className="max-w-[1100px] mx-auto px-4 lg:px-10 py-20" data-testid={testid}>
    <div className="max-w-3xl mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px w-10 bg-[#b91c1c]" />
        <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">{kicker}</span>
      </div>
      <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
        {title}
      </h1>
      <p className="text-[12px] tracking-[0.18em] uppercase text-gray-400 mt-6">Last updated · {lastUpdated}</p>
    </div>
    {children}
  </div>
);

export const Terms = () => (
  <LegalLayout kicker="Legal" title="Terms of Service." lastUpdated="February 2026" testid="terms-page">
    <Section title="1. Acceptance of Terms">
      <p>By accessing or using the Certified Wellness website (the "Site"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Site.</p>
    </Section>
    <Section title="2. Eligibility & Research Use">
      <p>All products sold on this Site are intended <strong>FOR LABORATORY RESEARCH USE ONLY</strong>. They are not drugs, food, cosmetics, or dietary supplements. They are not intended for human or animal consumption. You represent that you are at least 18 years of age and a qualified researcher, scientist, or licensed professional acquiring these products solely for in vitro research purposes.</p>
    </Section>
    <Section title="3. Account & Security">
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. Notify us immediately of any unauthorized use.</p>
    </Section>
    <Section title="4. Orders & Payment">
      <p>All orders are subject to acceptance and product availability. Prices and availability may change without notice. Payment is processed securely through our payment processor and we never store your full card details on our servers.</p>
    </Section>
    <Section title="5. Shipping & Risk of Loss">
      <p>Title and risk of loss for products pass to you upon our delivery to the carrier. Estimated delivery times are not guaranteed.</p>
    </Section>
    <Section title="6. Disclaimer of Warranties">
      <p>Products and the Site are provided on an "as-is" and "as-available" basis. We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement, except as may be expressly required by applicable law.</p>
    </Section>
    <Section title="7. Limitation of Liability">
      <p>To the maximum extent permitted by law, Certified Wellness shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Site or products.</p>
    </Section>
    <Section title="8. Intellectual Property">
      <p>All content on the Site — including text, graphics, logos, images, and code — is the property of Certified Wellness or its licensors and is protected by copyright, trademark, and other applicable laws.</p>
    </Section>
    <Section title="9. Governing Law">
      <p>These Terms are governed by the laws of the United States and the state in which Certified Wellness is registered, without regard to conflict-of-law principles.</p>
    </Section>
    <Section title="10. Changes">
      <p>We may update these Terms at any time. Continued use of the Site after changes constitutes your acceptance of the updated Terms.</p>
    </Section>
    <Section title="11. Contact">
      <p>Questions about these Terms? Reach our team via the Contact page.</p>
    </Section>
  </LegalLayout>
);

export const Privacy = () => (
  <LegalLayout kicker="Legal" title="Privacy Policy." lastUpdated="February 2026" testid="privacy-page">
    <Section title="1. Information We Collect">
      <p>We collect information you provide when placing an order, creating an account, contacting support, or subscribing to our newsletter. This includes name, shipping address, email, phone, and order history. Payment details are handled by our payment processor and never stored on our servers.</p>
    </Section>
    <Section title="2. How We Use It">
      <p>We use your information to fulfill orders, communicate about your purchase, provide customer support, send marketing emails (only if you opt in), prevent fraud, and improve the Site.</p>
    </Section>
    <Section title="3. Sharing">
      <p>We never sell your personal information. We share it only with vetted service providers strictly to fulfill orders (e.g., payment processing, shipping carriers, email delivery), and with authorities if required by law.</p>
    </Section>
    <Section title="4. Cookies">
      <p>We use a minimal set of cookies for cart persistence, login sessions, age-verification, and basic analytics. You can control cookies through your browser at any time.</p>
    </Section>
    <Section title="5. Data Retention">
      <p>We retain order records for as long as necessary to comply with tax and legal obligations. Marketing data is retained until you unsubscribe.</p>
    </Section>
    <Section title="6. Your Rights">
      <p>You have the right to access, correct, or request deletion of your personal data. Contact us via the Contact page to exercise these rights.</p>
    </Section>
    <Section title="7. Security">
      <p>We use SSL encryption, hashed credentials, and least-privilege access controls. No system is 100% secure, and we cannot guarantee absolute security of your data.</p>
    </Section>
    <Section title="8. Contact">
      <p>For privacy questions, contact our team through the Contact page.</p>
    </Section>
  </LegalLayout>
);

export const Refund = () => (
  <LegalLayout kicker="Legal" title="Refund Policy." lastUpdated="February 2026" testid="refund-page">
    <Section title="Our Commitment">
      <p>Customer satisfaction is critical. We offer refunds and replacements under the conditions below to balance fairness with the unique requirements of research compounds.</p>
    </Section>
    <Section title="Damaged or Incorrect Items">
      <p>If your order arrives damaged, defective, or contains the wrong product, contact us within 7 days of delivery with a photo. We will issue a free replacement or full refund.</p>
    </Section>
    <Section title="Unopened Returns">
      <p>Unopened, factory-sealed vials may be returned within 14 days of delivery for a refund (less original shipping). Buyer is responsible for return shipping. Items must be in original, unaltered packaging.</p>
    </Section>
    <Section title="Non-Returnable Items">
      <p>Due to the nature of research compounds, we cannot accept returns of opened vials, reconstituted product, custom orders, or sale-final items.</p>
    </Section>
    <Section title="Refund Processing">
      <p>Approved refunds are issued to your original payment method within 5–10 business days. You'll receive an email confirmation when the refund is processed.</p>
    </Section>
    <Section title="Lost or Stolen Packages">
      <p>We provide tracked shipping on every order. If a package is marked delivered but missing, contact us — we will work with the carrier to investigate and resolve.</p>
    </Section>
    <Section title="How to Request">
      <p>Email support or use the Contact page with your order number, photos (if applicable), and a brief description. Most requests are resolved within 24 hours.</p>
    </Section>
  </LegalLayout>
);

export const Disclaimer = () => (
  <LegalLayout kicker="Compliance" title="Research Disclaimer." lastUpdated="February 2026" testid="disclaimer-page">
    <div className="bg-[#fafbfc] border-l-4 border-[#b91c1c] p-6 mb-8">
      <p className="text-[#0a0a0a] font-semibold tracking-[0.05em] uppercase text-sm">For Laboratory Research Use Only</p>
      <p className="text-gray-700 mt-2 font-light leading-relaxed">
        All products sold by Certified Wellness are intended exclusively for in vitro laboratory research. They are not for human or animal consumption, are not drugs, foods, cosmetics, or dietary supplements, and have not been evaluated or approved by the FDA.
      </p>
    </div>

    <Section title="Statement of Regulatory Status">
      <p>
        All products on this site are for research and development use only. Products are not for human consumption of any kind. The statements made on this website have not been evaluated by the U.S. Food and Drug Administration. The statements and the products of this company are not intended to diagnose, treat, cure, or prevent any disease.
      </p>
      <p>
        <strong className="text-[#0a0a0a]">Certified Wellness is a chemical supplier.</strong> Certified Wellness is <em>not</em> a compounding pharmacy or chemical compounding facility as defined under Section 503A of the Federal Food, Drug, and Cosmetic Act. Certified Wellness is <em>not</em> an outsourcing facility as defined under Section 503B of the Federal Food, Drug, and Cosmetic Act.
      </p>
      <p>
        All products are sold for research, laboratory, or analytical purposes only, and are not for human consumption.
      </p>
    </Section>

    <Section title="Researcher Acknowledgment">
      <p>By purchasing from this Site you affirm that you are at least 18 years of age and a qualified researcher, scientist, clinician, or licensed professional acquiring these products solely for legitimate, in vitro research purposes.</p>
    </Section>
    <Section title="No Medical Claims">
      <p>Nothing on this Site constitutes medical advice, diagnosis, or treatment. References to peptides and research compounds are descriptive only and are not endorsements or recommendations for human use.</p>
    </Section>
    <Section title="Compliance Responsibility">
      <p>You are solely responsible for ensuring your purchase, possession, and use of these compounds complies with all federal, state, and local laws applicable to you. Certified Wellness assumes no liability for misuse.</p>
    </Section>
    <Section title="Documentation">
      <p>Each batch ships with a Certificate of Analysis on file. Researchers are encouraged to document and follow standard laboratory safety protocols (PPE, sterile technique, sharps disposal) when handling research compounds.</p>
    </Section>
  </LegalLayout>
);
